import { useDifficulties } from "~/hooks/useDifficulties"
import { difficultyLabel } from "~/lib/difficulty"
import { EntityIdentity, UnassignedIdentity } from "~/components/EntityIdentity"
import { RelativeTimestamp } from "~/components/RelativeTimestamp"

import { DifficultyIcon } from "~/components/icons/difficulty"
import { PriorityIcon } from "~/components/icons/priority"
import { StatusIcon } from "~/components/icons/status"
import { Marquee } from "~/components/ui/marquee"
import { Checkbox } from "~/components/ui/checkbox"
import { IssueLabelShowcase } from "~/components/issueDetailsPage/IssueLabelShowcase"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import { usePermission } from "~/hooks/usePermission"
import type { IssueDifficulty, IssuePriority, IssueStatus } from "~/lib/enums"
import { TeamPermissions } from "~/lib/permissions"
import { cn, enumFormat, fancyIssueId, shortRelativeTime } from "~/lib/utils"
import { api, type RouterOutputs } from "~/trpc/react"

import {
	IssueSetting,
	issueSettingAssignees
} from "../issueDetailsPage/IssueSetting"
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from "../ui/tooltip"

export const issueRowProperties = [
	{ id: "identifier", label: "Issue ID" },
	{ id: "labels", label: "Labels" },
	{ id: "status", label: "Status" },
	{ id: "priority", label: "Priority" },
	{ id: "difficulty", label: "Difficulty" },
	{ id: "assignee", label: "Assignee" },
	{ id: "updatedAt", label: "Last activity" }
] as const

export function IssueRow({
	issue,
	hiddenProperties = [],
	selected = false,
	onSelectionChange,
	selectionDisabled = false,
	disabled = false,
	onOpen,
	practice = false
}: {
	practice?: boolean
	issue: Pick<
		RouterOutputs["issues"]["getTeamIssues"]["issues"][number],
		| "id"
		| "teamId"
		| "friendlyId"
		| "title"
		| "status"
		| "priority"
		| "difficulty"
		| "assignee"
		| "labels"
		| "updatedAt"
	> & {
		team: Pick<
			RouterOutputs["issues"]["getTeamIssues"]["issues"][number]["team"],
			"slug" | "organization"
		>
	}
	hiddenProperties?: (typeof issueRowProperties)[number]["id"][]
	selected?: boolean
	onSelectionChange?: (selected: boolean) => void
	selectionDisabled?: boolean
	onOpen?: () => void
	disabled?: boolean
}) {
	const showLabels =
		!hiddenProperties.includes("labels") && issue.labels.length > 0
	const showStatus = !hiddenProperties.includes("status")
	const showUpdatedAt = !hiddenProperties.includes("updatedAt")
	const showProperties = issueRowProperties.some(
		({ id }) =>
			id !== "identifier" &&
			!hiddenProperties.includes(id) &&
			(id !== "labels" || issue.labels.length > 0)
	)
	const utils = api.useUtils()
	const { data: issueTeam } = api.team.get.useQuery(
		{ teamId: issue.teamId },
		{ enabled: !practice }
	)
	const difficulties = useDifficulties(issue.teamId)
	const { hasTeamPermission, teamLoaded } = usePermission({
		activeTeamId: issue.teamId
	})
	const members = issueTeam?.teamMembers
	const canEdit =
		!practice &&
		!disabled &&
		teamLoaded &&
		hasTeamPermission(TeamPermissions.EDIT_ISSUES)
	const editIssue = api.issues.edit.useMutation({
		onError: (error) => toast.error(error.message || "Failed to update issue"),
		onSettled: (_data, _error, variables) => {
			void utils.issues.getTeamIssues.invalidate()
			void utils.issues.getUserIssues.invalidate()
			if (variables.status !== undefined) {
				void utils.user.getTeams.invalidate()
				void utils.favorites.list.invalidate()
			}
		}
	})
	const updateIssue = (
		change:
			| { status: IssueStatus }
			| { priority: IssuePriority }
			| { difficulty: IssueDifficulty }
			| { assigneeId: string | null }
	) => {
		editIssue.mutate({ id: issue.id, teamId: issue.teamId, ...change })
	}

	return (
		<TooltipProvider>
			<div
				className={cn(
					"group @container/issue-row flex min-h-12 min-w-0 items-center gap-3 pl-2 pr-4 py-2 text-sm transition-colors border-t border-stroke/30 first:border-t-0 hover:bg-active/60",
					selected && "bg-active/60"
				)}
			>
				{onSelectionChange && (
					<span
						className={cn(
							"-mr-1.5 flex shrink-0 items-center transition-opacity group-hover:opacity-100 focus-within:opacity-100",
							selected ? "opacity-100" : "opacity-0"
						)}
						title={
							selectionDisabled
								? "Select up to 100 issues from one team at a time"
								: !canEdit && !disabled
									? "You don't have permission to edit this issue"
									: undefined
						}
					>
						<Checkbox
							checked={selected}
							disabled={!canEdit || selectionDisabled}
							aria-label={`Select ${fancyIssueId(issue.friendlyId, issue.team.slug)}: ${issue.title}`}
							onCheckedChange={(checked) => onSelectionChange(checked === true)}
						/>
					</span>
				)}
				<div className="min-w-0 flex-1">
					<div className="flex min-w-0 flex-col gap-1 @lg/issue-row:flex-row @lg/issue-row:items-center @lg/issue-row:gap-3">
						{!hiddenProperties.includes("identifier") && (
							<span className="w-24 shrink-0 truncate font-mono text-[11px] text-text-small">
								{fancyIssueId(issue.friendlyId, issue.team.slug)}
							</span>
						)}
						<Link
							to={
								practice
									? "#"
									: `/${issue.team.organization.slug}/${issue.team.slug}/${onOpen ? `queue?issue=${issue.friendlyId}` : issue.friendlyId}`
							}
							onClick={(event) => {
								if (
									onOpen &&
									event.button === 0 &&
									!event.metaKey &&
									!event.ctrlKey &&
									!event.shiftKey &&
									!event.altKey
								) {
									event.preventDefault()
									onOpen()
								}
							}}
							className="min-w-0 max-w-full flex-1 font-medium text-text-main hover:text-text-main hover:underline focus-visible:outline-2 focus-visible:outline-ring"
						>
							<Marquee>{issue.title}</Marquee>
						</Link>
					</div>
					{(showLabels || showStatus || showUpdatedAt) && (
						<div
							className={cn(
								"mt-1 flex min-w-0 flex-nowrap items-center gap-1.5 @3xl/issue-row:hidden",
								!showLabels && "@lg/issue-row:hidden"
							)}
						>
							{showStatus && (
								<span className="shrink-0 whitespace-nowrap text-xs text-text-small @lg/issue-row:hidden">
									{enumFormat(issue.status)}
								</span>
							)}
							{showLabels && (
								<IssueLabelShowcase labels={issue.labels} size="compact" />
							)}
							{showUpdatedAt && (
								<RelativeTimestamp
									date={issue.updatedAt}
									label="Last activity"
									formatRelativeTimestamp={shortRelativeTime}
									className="shrink-0 whitespace-nowrap text-xs text-text-small @lg/issue-row:hidden"
								/>
							)}
						</div>
					)}
				</div>

				{showProperties && (
					<div className="hidden shrink-0 items-center justify-end gap-1.5 @lg/issue-row:flex">
						{showLabels && (
							<div className="hidden max-w-[45cqw] min-w-0 shrink-0 items-center justify-end gap-1.5 @3xl/issue-row:flex">
								<IssueLabelShowcase labels={issue.labels} size="compact" />
							</div>
						)}

						{!hiddenProperties.includes("status") && (
							<Tooltip>
								<TooltipTrigger asChild>
									<div className="relative z-10">
										<IssueSetting
											value={issue.status}
											onChange={(status) => updateIssue({ status })}
											options="status"
											ariaLabel={`Status: ${enumFormat(issue.status)}`}
											activeTeamId={issue.teamId}
											disabled={!canEdit || editIssue.isPending}
											size="icon-md"
											trigger={<StatusIcon status={issue.status} />}
										/>
									</div>
								</TooltipTrigger>
								<TooltipContent>
									Status: {enumFormat(issue.status)}
								</TooltipContent>
							</Tooltip>
						)}

						{!hiddenProperties.includes("priority") && (
							<Tooltip>
								<TooltipTrigger asChild>
									<div className="relative z-10">
										<IssueSetting
											value={issue.priority}
											onChange={(priority) => updateIssue({ priority })}
											options="priority"
											ariaLabel={`Priority: ${enumFormat(issue.priority)}`}
											activeTeamId={issue.teamId}
											disabled={!canEdit || editIssue.isPending}
											size="icon-md"
											trigger={<PriorityIcon priority={issue.priority} />}
										/>
									</div>
								</TooltipTrigger>
								<TooltipContent>
									Priority: {enumFormat(issue.priority)}
								</TooltipContent>
							</Tooltip>
						)}

						{!hiddenProperties.includes("difficulty") && (
							<Tooltip>
								<TooltipTrigger asChild>
									<div className="relative z-10">
										<IssueSetting
											value={issue.difficulty}
											onChange={(difficulty) => updateIssue({ difficulty })}
											options="difficulty"
											ariaLabel={`Difficulty: ${difficultyLabel(issue.difficulty, difficulties)}`}
											activeTeamId={issue.teamId}
											disabled={!canEdit || editIssue.isPending}
											size="icon-md"
											trigger={
												<DifficultyIcon
													difficulty={issue.difficulty}
													teamId={issue.teamId}
												/>
											}
										/>
									</div>
								</TooltipTrigger>
								<TooltipContent>
									Difficulty: {difficultyLabel(issue.difficulty, difficulties)}
								</TooltipContent>
							</Tooltip>
						)}

						{!hiddenProperties.includes("assignee") && (
							<div className="relative z-10 flex items-center">
								{issue.assignee ? (
									<Tooltip>
										<TooltipTrigger asChild>
											<IssueSetting
												value={issue.assignee.id}
												ariaLabel={`Assignee: ${issue.assignee.orgMember.user.name}`}
												onChange={(assigneeId) => updateIssue({ assigneeId })}
												options={issueSettingAssignees(members)}
												withSearch
												activeTeamId={issue.teamId}
												disabled={!canEdit || editIssue.isPending}
												trigger={
													<EntityIdentity
														kind="user"
														variant="avatar"
														size="md"
														name={issue.assignee.orgMember.user?.name ?? "?"}
														image={issue.assignee.orgMember.user?.image}
														lastSeenAt={
															issue.assignee.orgMember.user?.lastSeenAt
														}
														className="ring-2 ring-background-secondary"
													/>
												}
												size="icon-md"
											/>
										</TooltipTrigger>
										<TooltipContent>
											Assignee: {issue.assignee?.orgMember.user?.name}
										</TooltipContent>
									</Tooltip>
								) : (
									<Tooltip>
										<TooltipTrigger asChild>
											<IssueSetting
												value={null}
												ariaLabel="Assignee: Unassigned"
												onChange={(assigneeId) => updateIssue({ assigneeId })}
												options={issueSettingAssignees(members)}
												withSearch
												activeTeamId={issue.teamId}
												disabled={!canEdit || editIssue.isPending}
												trigger={<UnassignedIdentity size="lg" />}
												size="icon-md"
											/>
										</TooltipTrigger>
										<TooltipContent>Unassigned</TooltipContent>
									</Tooltip>
								)}
							</div>
						)}

						{showUpdatedAt && (
							<RelativeTimestamp
								date={issue.updatedAt}
								label="Last activity"
								formatRelativeTimestamp={shortRelativeTime}
								className="w-10 justify-end whitespace-nowrap text-[11px] tabular-nums text-text-small"
							/>
						)}
					</div>
				)}
			</div>
		</TooltipProvider>
	)
}
